export const VENDOR_TIMEOUT_MS = 20000;

export const FIDES_INITIALIZED = 'FidesInitialized';
export const FIDES_READY = 'FidesReady';
export const FIDES_UPDATED = 'FidesUpdated';

/**
 * Events after which `window.Fides.consent` is safe to read
 */
export const FIDES_CONSENT_EVENTS = [
  FIDES_INITIALIZED,
  FIDES_READY,
  FIDES_UPDATED,
] as const;

export type ConsentCategories = {
  functional?: boolean;
  social_media?: boolean;
  marketing?: boolean;
  analytics?: boolean;
  strictly_necessary?: boolean;
};

export type SegmentIntegrations = Record<string, boolean>;

type GatedCategory = 'analytics' | 'marketing' | 'social_media';

/**
 * Categories a Global Privacy Control signal withdraws. GPC is an opt-out of
 * the sale/sharing of personal data, which is what the advertising and
 * social-platform vendors do, so it denies `marketing` and `social_media`.
 * It leaves `analytics` alone.
 */
export const GPC_DENIED_CATEGORIES: readonly GatedCategory[] = [
  'marketing',
  'social_media',
];

/**
 * Whether the browser is sending a Global Privacy Control signal.
 */
export const isGpcEnabled = (): boolean =>
  typeof navigator !== 'undefined' &&
  (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl ===
    true;

/**
 * The consent we act on: Fides' resolved preferences with GPC overlaid.
 */
export const toEffectiveConsent = (
  consent: ConsentCategories | undefined,
): ConsentCategories | undefined => {
  if (!consent || !isGpcEnabled()) {
    return consent;
  }
  const effective: ConsentCategories = { ...consent };
  GPC_DENIED_CATEGORIES.forEach(category => {
    effective[category] = false;
  });
  return effective;
};

/**
 * A destination/vendor is allowed only when **every** category it is mapped to
 * is explicitly granted. Absent or unresolved consent counts as denied, so the
 * gate fails closed. A GPC signal denies the categories in
 * `GPC_DENIED_CATEGORIES` whatever Fides has saved.
 */
export const hasCategoryConsent = (
  consent: ConsentCategories | undefined,
  categories: readonly GatedCategory[],
): boolean => {
  const effective = toEffectiveConsent(consent);
  return categories.every(category => effective?.[category] === true);
};
const DESTINATION_CATEGORIES: Record<string, readonly GatedCategory[]> = {
  Amplitude: ['analytics'],
  Optimizely: ['analytics'],
  'Bing Ads': ['marketing'],
  'Google Tag Manager': ['marketing'],
  Facebook: ['marketing', 'social_media'],
  'Facebook Pixel': ['marketing', 'social_media'],
  'TikTok Pixel': ['marketing', 'social_media'],
};

/**
 * Vendors we gate at runtime through their own consent APIs (`ttq`, `fbq`)
 * rather than through Segment's `integrations` filter. These are the social
 * platforms, so they require `social_media` on top of `marketing` — rejecting
 * social media alone is enough to revoke them, which mirrors the
 * `DESTINATION_CATEGORIES` entries for the same vendors above.
 *
 * Purely analytics/advertising vendors (Microsoft UET, Clarity, Google consent
 * mode) keep their existing inline category checks; they have no social-media
 * dimension.
 */
export type SocialVendor = 'facebook' | 'pinterest' | 'tiktok';

export const SOCIAL_VENDOR_CATEGORIES: Record<SocialVendor, readonly GatedCategory[]> = {
  facebook: ['marketing', 'social_media'],
  pinterest: ['marketing', 'social_media'],
  tiktok: ['marketing', 'social_media'],
};

/**
 * Whether a social vendor may track. Reads `window.Fides.consent` shape, so
 * callers can pass `window.Fides?.consent` directly.
 */
export const hasSocialVendorConsent = (
  consent: ConsentCategories | undefined,
  vendor: SocialVendor,
): boolean => hasCategoryConsent(consent, SOCIAL_VENDOR_CATEGORIES[vendor]);

export type ConsentStorageState = 'granted' | 'denied';

const toStorageState = (
  consent: ConsentCategories | undefined,
  categories: readonly GatedCategory[],
): ConsentStorageState =>
  hasCategoryConsent(consent, categories) ? 'granted' : 'denied';

export type UetConsentPayload = { ad_storage: ConsentStorageState };

export const toUetConsentPayload = (
  consent: ConsentCategories | undefined,
): UetConsentPayload => ({
  ad_storage: toStorageState(consent, ['marketing']),
});

export type ClarityConsentPayload = {
  ad_Storage: ConsentStorageState;
  analytics_Storage: ConsentStorageState;
};

export const toClarityConsentPayload = (
  consent: ConsentCategories | undefined,
): ClarityConsentPayload => ({
  ad_Storage: toStorageState(consent, ['marketing']),
  analytics_Storage: toStorageState(consent, ['analytics']),
});

export const toDisabledSegmentIntegrations = (
  consent: ConsentCategories | undefined,
): SegmentIntegrations => {
  const integrations: SegmentIntegrations = {};
  Object.entries(DESTINATION_CATEGORIES).forEach(([destination, categories]) => {
    if (!hasCategoryConsent(consent, categories)) {
      integrations[destination] = false;
    }
  });
  return integrations;
};

export type SegmentLoadOptions = { integrations: SegmentIntegrations };

/**
 * Options for `analytics.load()`.
 */
export const toSegmentLoadOptions = (
  consent: ConsentCategories | undefined,
): SegmentLoadOptions => ({
  integrations: toDisabledSegmentIntegrations(consent),
});

/**
 * Whether to load Segment at all. Under GPC only `analytics` can justify the
 * load, since `marketing` is withdrawn by the signal.
 */
export const hasSegmentLoadConsent = (
  consent: ConsentCategories | undefined,
): boolean => {
  const effective = toEffectiveConsent(consent);
  return effective?.analytics === true || effective?.marketing === true;
};

export type SegmentConsentContext = {
  context: { consent: { categoryPreferences: Required<ConsentCategories> } };
};

/**
 * The `context.consent` payload for Segment's consent enforcement, built from
 * the effective consent (GPC included)
 * https://segment.com/docs/privacy/consent-management/consent-in-segment-connections/#consent-object
 */
export const toSegmentConsentContext = (
  consent: ConsentCategories | undefined,
): SegmentConsentContext => {
  const effective = toEffectiveConsent(consent);
  return {
    context: {
      consent: {
        categoryPreferences: {
          functional: effective?.functional ?? false,
          social_media: effective?.social_media ?? false,
          marketing: effective?.marketing ?? false,
          analytics: effective?.analytics ?? false,
          strictly_necessary: effective?.strictly_necessary ?? false,
        },
      },
    },
  };
};
